import React, { useState, useRef, useCallback } from "react";

export default function ProductGallery({ productId, baseAngle = 130, images: realImages }) {
  const [idx, setIdx] = useState(0);
  const [zoom, setZoom] = useState(false);
  const [zoomPos, setZoomPos] = useState({ x: 50, y: 50 });
  const frameRef = useRef(null);
  const hasReal = realImages && realImages.length > 0;
  const placeholders = [
    { angle: baseAngle, c1: "#1c1c22", c2: "#0a0a0c" },
    { angle: baseAngle + 40, c1: "#161620", c2: "#050510" },
    { angle: baseAngle - 30, c1: "#1a1620", c2: "#0a080d" },
  ];
  const images = hasReal ? realImages : placeholders;

  // Desktop-only magnify: track cursor position as a % within the frame,
  // then scale + offset a zoomed copy of the image via background-position.
  const onMouseMove = useCallback((e) => {
    const el = frameRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const x = Math.max(0, Math.min(100, ((e.clientX - r.left) / r.width) * 100));
    const y = Math.max(0, Math.min(100, ((e.clientY - r.top) / r.height) * 100));
    setZoomPos({ x, y });
  }, []);

  return (
    <div data-testid={`gallery-${productId}`}>
      <div className="mt-glass mt-rainbow-edge rounded-[22px] p-3">
        <div
          ref={frameRef}
          className="relative mt-figure aspect-[4/5] flex items-center justify-center overflow-hidden cursor-zoom-in"
          onMouseEnter={() => hasReal && setZoom(true)}
          onMouseLeave={() => setZoom(false)}
          onMouseMove={onMouseMove}
          data-testid="gallery-zoom-frame"
        >
          {images.map((img, i) => (
            <div key={i} className="absolute inset-0 flex items-center justify-center transition-opacity duration-300"
              style={{ background: hasReal ? "none" : `linear-gradient(${img.angle}deg, ${img.c1} 0%, ${img.c2} 100%)`, opacity: i === idx ? 1 : 0 }}
              aria-hidden={i !== idx}>
              {hasReal ? (
                <img src={img} alt={`View ${i+1}`} className="w-full h-full object-cover" loading={i===0?"eager":"lazy"} onError={(e)=>{e.currentTarget.style.display="none";}}/>
              ) : (
                <span className="font-display font-semibold select-none" style={{ color:"#F5F5F5", opacity:0.2, fontSize:"clamp(6rem,14vw,12rem)", letterSpacing:"-0.06em" }}>MT</span>
              )}
            </div>
          ))}
          {!hasReal && <span aria-hidden="true" className="absolute inset-0 opacity-[0.06] pointer-events-none" style={{ backgroundImage:"repeating-linear-gradient(180deg, rgba(255,255,255,0.7) 0 1px, transparent 1px 3px)" }}/>}

          {/* Magnified preview — desktop hover only, hidden on touch via CSS */}
          {hasReal && zoom && (
            <div
              className="mt-gallery-zoom-pane"
              aria-hidden="true"
              style={{
                backgroundImage: `url(${images[idx]})`,
                backgroundPosition: `${zoomPos.x}% ${zoomPos.y}%`,
              }}
            />
          )}
          {hasReal && (
            <span className="mt-gallery-zoom-hint font-mono text-[0.55rem] tracking-mt uppercase" aria-hidden="true">
              Hover to zoom
            </span>
          )}
        </div>
      </div>
      <div className="grid grid-cols-3 gap-3 mt-4">
        {images.map((img, i) => (
          <button key={i} type="button" onClick={() => setIdx(i)}
            className={`mt-glass rounded-[14px] p-1.5 transition-all ${i===idx?"mt-rainbow-edge":""}`}
            style={{ borderColor: i===idx?"rgba(0,170,255,0.6)":"var(--mt-glass-border)", boxShadow: i===idx?"0 0 18px rgba(0,170,255,0.25)":"none" }}
            aria-label={`View ${i+1}`} data-testid={`gallery-thumb-${i}`}>
            <div className="mt-figure aspect-[4/5] flex items-center justify-center overflow-hidden"
              style={{ background: hasReal?"#0a0a0c":`linear-gradient(${img.angle}deg, ${img.c1} 0%, ${img.c2} 100%)` }}>
              {hasReal
                ? <img src={img} alt="" className="w-full h-full object-cover" loading="lazy" onError={(e)=>{e.currentTarget.style.display="none";}}/>
                : <span className="font-display font-semibold select-none" style={{color:"#F5F5F5",opacity:0.2,fontSize:"2.2rem",letterSpacing:"-0.06em"}}>MT</span>
              }
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}
